import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PlusIcon, XIcon } from '@shopify/polaris-icons';
import { Ic } from '../ui';
import { splitRecipients } from './status';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// Edits who a renamed account's continuity group forwards to. Only opened
// for runs that set forwarding up; at least one address has to stay.
// forwardTo is the run's raw string so the draft only resets when it
// actually changes, not on every poll of the page.
function ForwardingDialog({ open, groupEmail, forwardTo, onSave, onClose }) {
  const current = useMemo(() => splitRecipients(forwardTo).map((a) => a.toLowerCase()), [forwardTo]);
  const ref = useRef(null);
  const [draft, setDraft] = useState(current);
  const [adding, setAdding] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (open) {
      setDraft(current);
      setAdding('');
      setError('');
      if (dlg.showModal && !dlg.open) dlg.showModal(); else dlg.setAttribute('open', '');
    } else if (dlg.open) {
      dlg.close();
    }
  }, [open, current]);

  const add = () => {
    const v = adding.trim().toLowerCase();
    if (!EMAIL_RE.test(v)) { setError('Enter a full email address, like name@soccerpost.com.'); return; }
    if (!v.endsWith('@soccerpost.com')) { setError('Forwarding only works to @soccerpost.com addresses.'); return; }
    if (v === groupEmail.toLowerCase()) { setError("The group can't forward to its own address."); return; }
    if (draft.includes(v)) { setError(`${v} is already on the list.`); return; }
    setError('');
    setDraft([...draft, v]);
    setAdding('');
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave(draft);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const unchanged = draft.join() === current.join();

  return (
    <dialog className="uh-dlg" ref={ref} aria-labelledby="fwd-h" onCancel={onClose} onClick={(e) => { if (e.target === ref.current) onClose(); }}>
      <div className="uh-dlg-h">
        <h2 id="fwd-h">Change mail forwarding</h2>
        <button type="button" className="uh-ibtn" aria-label="Close" onClick={onClose}><Ic source={XIcon} /></button>
      </div>
      <div className="uh-dlg-b">
        <p>Mail sent to <b className="uh-mono">{groupEmail}</b> goes to everyone below. At least one address has to stay.</p>
        <ul className="uh-rcpts">
          {draft.map((addr) => (
            <li key={addr} className={current.includes(addr) ? '' : 'added'}>
              <span className="uh-mono">{addr}</span>
              <button
                type="button"
                className="uh-ibtn"
                aria-label={`Remove ${addr}`}
                title={draft.length < 2 ? 'At least one address has to stay' : undefined}
                disabled={draft.length < 2 || saving}
                onClick={() => setDraft(draft.filter((a) => a !== addr))}
              >
                <Ic source={XIcon} sm />
              </button>
            </li>
          ))}
        </ul>
        <div className="uh-fld">
          <label htmlFor="fwd-add">Add someone</label>
          <div className="uh-fld-row">
            <input
              id="fwd-add"
              className="uh-inp"
              type="email"
              autoComplete="off"
              placeholder="name@soccerpost.com"
              value={adding}
              disabled={saving}
              onChange={(e) => setAdding(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            />
            <button type="button" className="uh-btn" disabled={saving} onClick={add}><Ic source={PlusIcon} sm />Add</button>
          </div>
          {error && <span className="uh-help uh-err">{error}</span>}
        </div>
      </div>
      <div className="uh-dlg-f">
        <button type="button" className="uh-btn" onClick={onClose} disabled={saving}>Cancel</button>
        <button type="button" className="uh-btn primary" disabled={unchanged || saving} onClick={save}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </dialog>
  );
}

export default ForwardingDialog;
